// reportInvestigationList.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import "dayjs/locale/th";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import {
  getDebt,
  getRemainDebt,
  loadLoanByContno,
} from "../../reportSummaryJobsLawyers/lawyerCaseUtils";

dayjs.locale("th");

// ---------- helpers ----------
const toThaiDate = (d) => {
  const x = dayjs(d);
  if (!x.isValid()) return "-";
  const dd = x.format("D");
  const mmm = x.locale("th").format("MMM");
  const yyyyBE = x.year() + 543;
  return `${dd} ${mmm} ${yyyyBE}`;
};

const fmtMoney = (n) =>
  Number(n ?? 0).toLocaleString("th-TH", { maximumFractionDigits: 0 });

const fmtMoney2 = (n) =>
  n === null || n === undefined || n === "" || isNaN(Number(n))
    ? "-"
    : Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const propertyTypeLabel = (id) =>
  id === 1 ? "น.ส.4จ." : id === 2 ? "น.ส.3ก." : "-";

const propertyTypeDetailLabel = (id) =>
  id === 1
    ? "ที่ดินว่างเปล่า"
    : id === 2
    ? "ที่ตาบอด"
    : id === 3
    ? "ที่ดินพร้อมสิ่งปลูกสร้าง"
    : id === 4
    ? "ที่นา"
    : id === 5
    ? "ห้องชุด"
    : id === 6
    ? "บ้านเอื้ออาทร"
    : id === 7
    ? "ที่ดินต้องห้าม"
    : id === 8
    ? "จดรวม 2 โฉนด"
    : id === 9
    ? "ที่ถนนทางเข้า"
    : id === 10
    ? "ไม่ได้ลงพื้นที่ตรวจสอบ"
    : "-";

const fmtArea = (rai, ngan, wa, subwa) => {
  const parts = [];
  if (rai) parts.push(`${rai} ไร่`);
  if (ngan) parts.push(`${ngan} งาน`);
  if (wa) parts.push(`${wa} ตร.วา`);
  if (subwa) parts.push(`${subwa} ส่วนวา`);
  return parts.join(" ") || "-";
};

// นับเวลาถอยหลังถึงวันหมดอายุความ
const fmtCountdown = (expire) => {
  let cur = dayjs().startOf("day");
  const end = dayjs(expire).startOf("day");
  if (!end.isValid()) return "-";
  if (!end.isAfter(cur)) return "หมดอายุความแล้ว";
  const y = end.diff(cur, "year");
  cur = cur.add(y, "year");
  const m = end.diff(cur, "month");
  cur = cur.add(m, "month");
  const d = end.diff(cur, "day");
  const parts = [];
  if (y) parts.push(`${y} ปี`);
  if (m) parts.push(`${m} เดือน`);
  if (d) parts.push(`${d} วัน`);
  return `เหลือ ${parts.join(" ")}`;
};

// ---------- table head ----------
const span2 = (content) => ({ content, rowSpan: 2 });
const tableHead = [
  [
    span2("ลำดับ"),
    span2("เลขสัญญา"),
    span2("วันที่สืบ"),
    span2("วันพิพากษา/\nวันหมดอายุความ"),
    span2("ประเภททรัพย์"),
    span2("รายละเอียดทรัพย์"),
    span2("เจ้าของทรัพย์"),
    span2("ติดภาระ"),
    span2("บังคับคดี"),
    { content: "ข้อมูลสัญญา", colSpan: 4 },
    span2("ผู้รับผิดชอบ"),
  ],
  ["ยอดหนี้", "จ่ายมาทั้งหมด", "ยอดหนี้คงเหลือ", "จ่ายล่าสุด"],
];

/**
 * แปลง rawList -> rows สำหรับ autoTable
 * loanByContno: ข้อมูลสัญญาจาก loadLoanByContno -> { CONTNO: item }
 */
function buildBodyRows(rawList = [], loanByContno = {}) {
  const rows = [];
  let rowNo = 1;

  (rawList || []).forEach((r) => {
    console.log("r", r);

    const contno =
      [
        r?.CONTNO ? `${r.CONTNO}` : null,
        r?.CUSTOMER_TNAME ? `${r.CUSTOMER_TNAME}${r.CUSTOMER_FNAME} ` : null,
        r?.CUSTOMER_LNAME ? `${r.CUSTOMER_LNAME}` : null,
      ]
        .filter(Boolean)
        .join("\n") || "-";
    const assigeName = r.LAWYER_NNAME;

    // ข้อมูลสัญญา: ยอดหนี้ / จ่ายมาทั้งหมด / ยอดหนี้คงเหลือ / จ่ายล่าสุด
    const db2 = loanByContno[r?.CONTNO?.trim()];
    const loanCols = [
      fmtMoney2(getDebt(db2)),
      db2 ? fmtMoney2(db2.LOAN?.SMPAY) : "-",
      fmtMoney2(getRemainDebt(db2)),
      db2?.LOAN?.LPAYD ? toThaiDate(db2.LOAN.LPAYD) : "-",
    ];

    const invDate =
      [
        r?.investigation_date ? toThaiDate(r?.investigation_date) : "-",
        r?.department_of_lands ? r?.department_of_lands : "-",
      ]
        .filter(Boolean)
        .join("\n") || "-";

    // อายุความบังคับคดี 10 ปี นับจากวันที่พิพากษา
    // เหลือไม่เกิน 1 ปี (รวมหมดอายุแล้ว) -> ตัวอักษรสีแดง
    const expireDate = r?.judge_date
      ? dayjs(r.judge_date).add(10, "year")
      : null;
    const judgeTxt = expireDate
      ? {
          content: [
            toThaiDate(r.judge_date),
            toThaiDate(expireDate),
            fmtCountdown(expireDate),
          ].join("\n"),
          styles: expireDate.isBefore(dayjs().add(1, "year"))
            ? { textColor: [220, 38, 38] }
            : {},
        }
      : "-";

    const props = Array.isArray(r?.property_list) ? r.property_list : [];

    if (props.length === 0) {
      rows.push([
        String(rowNo++),
        contno,
        invDate,
        judgeTxt,
        "-",
        "-",
        "-",
        "-",
        "-",
        ...loanCols,
        assigeName,
      ]);
      return;
    }

    props.forEach((p) => {
      console.log("p", p);

      const type = [
        p?.property_type_id ? propertyTypeLabel(p?.property_type_id) : "-",
        p?.property_detail_id
          ? `ลักษณะที่ ${propertyTypeDetailLabel(p?.property_detail_id)}`
          : "-",
      ];
      const detail =
        [
          p?.deed_number ? `${p.deed_number}` : null,
          fmtArea(p?.rai, p?.ngan, p?.wa, p?.subwa),
          p?.district_desc ? `อ.${p.district_desc}` : null,
          p?.province_desc ? `จ.${p.province_desc}` : null,
        ]
          .filter(Boolean)
          .join("\n") || "-";

      const ownerPoss =
        [p?.possessor ? `${p.possessor}` : null].filter(Boolean).join("\n") ||
        "-";

      const burden =
        [
          p?.mortgage_type ? `(${p.mortgage_type})` : null,
          p?.mortgagee ? `ผู้รับ: ${p.mortgagee}` : null,
          (p?.mortgage_balance ?? null) != null
            ? `${fmtMoney(p.mortgage_balance)} บาท`
            : null,
        ]
          .filter(Boolean)
          .join("\n") || "-";

      const enforce =
        [
          p?.sequestrate_status === 1 ? "มีคำสั่งยึด/อายัด" : null,
          (p?.estimated_enforce_price ?? null) != null
            ? `ประเมิน: ${fmtMoney(p.estimated_enforce_price)} บาท`
            : null,
          p?.legal_execution_office
            ? `สนง.บังคับคดี: ${p.legal_execution_office}`
            : null,
          p?.seize_date ? `ยึดเมื่อ: ${toThaiDate(p.seize_date)}` : null,
        ]
          .filter(Boolean)
          .join("\n") || "-";

      const invDate =
        [
          r?.investigation_date ? toThaiDate(r?.investigation_date) : "-",
          r?.department_of_lands ? r?.department_of_lands : "-",
        ]
          .filter(Boolean)
          .join("\n") || "-";

      rows.push([
        String(rowNo++),
        contno,
        invDate,
        judgeTxt,
        type,
        detail,
        ownerPoss,
        burden,
        enforce,
        ...loanCols,
        assigeName,
      ]);
    });
  });

  return rows;
}

// ---------- main ----------
export async function printInvestigationReportList(rawList = []) {
  // ดึงข้อมูลสัญญา ถ้าดึงไม่ได้ยังพิมพ์รายงานได้ (ช่องข้อมูลสัญญาเป็น "-")
  let loanByContno = {};
  try {
    loanByContno = await loadLoanByContno(rawList || []);
  } catch (error) {
    console.error("loadLoanByContno error:", error);
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = { l: 5, r: 5, t: 14, b: 12 };

  doc.setFont("THSarabunNew", "bold");

  const body = buildBodyRows(rawList, loanByContno);

  doc.autoTable({
    head: tableHead,
    body,
    startY: margin.t + 16, // หน้าแรกเว้นที่สำหรับหัวกระดาษ
    margin: { left: margin.l, right: margin.r, bottom: margin.b },
    styles: {
      font: "THSarabunNew",
      fontSize: 11, // ↓ ลดฟอนต์เพื่อให้ wrap ดีขึ้น
      overflow: "linebreak",
      cellPadding: 2,
      valign: "top",
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [30, 77, 140],
      textColor: 255,
      fontStyle: "normal",
      fontSize: 11,
      halign: "center",
    },
    // ✅ จัดสัดส่วนคอลัมน์ใหม่ (รวม ~286 มม. ภายในพื้นที่พิมพ์ 287 มม.)
    columnStyles: {
      0: { halign: "center", cellWidth: 9 }, // ลำดับ
      1: { halign: "center", cellWidth: 24 }, // เลขสัญญา
      2: { halign: "center", cellWidth: 22 }, // วันที่สืบ
      3: { halign: "center", cellWidth: 26 }, // วันพิพากษา/วันหมดอายุความ
      4: { halign: "center", cellWidth: 22 }, // ประเภททรัพย์
      5: { halign: "center", cellWidth: 26 }, // รายละเอียดทรัพย์
      6: { halign: "center", cellWidth: 22 }, // เจ้าของทรัพย์
      7: { halign: "center", cellWidth: 22 }, // ติดภาระ
      8: { halign: "center", cellWidth: 24 }, // บังคับคดี/ราคาประเมิน
      9: { halign: "right", cellWidth: 18 }, // ยอดหนี้
      10: { halign: "right", cellWidth: 18 }, // จ่ายมาทั้งหมด
      11: { halign: "right", cellWidth: 18 }, // ยอดหนี้คงเหลือ
      12: { halign: "center", cellWidth: 16 }, // จ่ายล่าสุด
      13: { halign: "center", cellWidth: 19 }, // ผู้รับผิดชอบ
    },
    tableWidth: pageW - margin.l - margin.r,
    didDrawPage: (data) => {
      // ✅ หัวกระดาษเฉพาะหน้าแรก
      if (data.pageNumber === 1) {
        doc.setFont("THSarabunNew", "bold");
        doc.setFontSize(18);
        doc.text("รายงานผลการสืบทรัพย์", pageW / 2, margin.t, {
          align: "center",
        });
        doc.setFontSize(12);
        doc.text(
          `วันที่พิมพ์: ${toThaiDate(new Date())}`,
          pageW / 2,
          margin.t + 8,
          {
            align: "center",
          }
        );
      }

      // ✅ เลขหน้าทุกหน้า
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(10);
      doc.text(
        `หน้า ${data.pageNumber}/${pageCount}`,
        pageW - margin.r,
        pageH - 6,
        {
          align: "right",
        }
      );
    },
  });

  // สรุปท้าย
  const endY = doc.lastAutoTable?.finalY || pageH - margin.b - 10;
  doc.setFontSize(12);
  doc.text(
    `รวมทั้งหมด: ${body.length.toLocaleString("th-TH")} `,
    margin.l,
    endY + 8
  );

  const fileName = `investigation_summary_${dayjs().format("YYYYMMDD")}.pdf`;
  try {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const w = window.open(url);
    if (w) w.onload = () => w.print();
    else doc.save(fileName);
  } catch {
    doc.save(fileName);
  }
}
